import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Clock, Shield, Smile } from 'lucide-react';
import SEO from '@/components/SEO';
import { financePageSEO } from '@/data/seo';
import FinanceCalculator from '@/components/FinanceCalculator';
import MiniConsultButton from '@/components/MiniConsultButton';

const Finance = () => {
  return (
    <>
      <SEO
        title={financePageSEO.title}
        description={financePageSEO.description}
        path={financePageSEO.path}
        keywords={financePageSEO.keywords}
        image={financePageSEO.image}
      />

      <div className="min-h-screen bg-sky-gray/30">
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                <div className="w-12 h-12 bg-gradient-to-br from-burnt-coral to-golden-sand rounded-full flex items-center justify-center shadow-lg">
                  <Smile className="w-7 h-7 text-white" />
                </div>
                <div>
                  <div className="text-rabbit font-bold text-2xl">Restore.Dental</div>
                  <div className="text-chocolate-malt text-xs">Dr. Kartik Patel</div>
                </div>
              </Link>

              <Link 
                to="/" 
                className="flex items-center gap-2 text-burnt-coral hover:text-golden-sand transition-colors font-semibold"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Back to Home</span>
              </Link>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-burnt-coral via-burnt-coral to-golden-sand text-white py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full text-sm font-semibold mb-6 animate-pulse">
                0% Interest • No Hidden Fees
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Making Treatment Affordable
              </h1>
              
              <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto">
                Spread the cost of your dental care with flexible, interest-free payment plans. 
                Transform your smile without the upfront cost.
              </p>

              <div className="flex flex-wrap gap-4 justify-center">
                <MiniConsultButton />
                <a 
                  href="https://lead.tabeo.co.uk/perfect-smile-acton/finance"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-burnt-coral px-8 py-4 rounded-full font-bold text-lg hover:bg-sky-gray transition-all shadow-lg hover:shadow-xl hover:scale-105"
                >
                  Apply Now via Tabeo
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Calculator Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-rabbit mb-4">
                Calculate Your Monthly Payment
              </h2>
              <p className="text-lg text-chocolate-malt max-w-2xl mx-auto">
                See how affordable your treatment can be. Adjust the cost and payment term to suit your budget.
              </p>
            </div>
            
            <FinanceCalculator />
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-rabbit text-center mb-16">
                How It Works
              </h2>

              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-burnt-coral to-golden-sand rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <span className="text-white text-3xl font-bold">1</span>
                  </div>
                  <h3 className="text-xl font-bold text-rabbit mb-3">Book Consultation</h3>
                  <p className="text-chocolate-malt">
                    Visit us for a consultation. We'll discuss your treatment plan and costs.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-burnt-coral to-golden-sand rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <span className="text-white text-3xl font-bold">2</span>
                  </div>
                  <h3 className="text-xl font-bold text-rabbit mb-3">Apply in 2 Minutes</h3>
                  <p className="text-chocolate-malt">
                    Complete a quick online application. Get an instant decision.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-burnt-coral to-golden-sand rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <span className="text-white text-3xl font-bold">3</span>
                  </div>
                  <h3 className="text-xl font-bold text-rabbit mb-3">Start Treatment</h3>
                  <p className="text-chocolate-malt">
                    Once approved, we can begin your treatment immediately.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 md:py-24 bg-sky-gray/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-rabbit text-center mb-12">
                Why Choose Our Finance Plans?
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all">
                  <CheckCircle2 className="w-12 h-12 text-burnt-coral mb-4" />
                  <h3 className="text-xl font-bold text-rabbit mb-2">0% Interest</h3>
                  <p className="text-chocolate-malt">
                    Absolutely no interest charges. What you borrow is exactly what you pay back.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all">
                  <Clock className="w-12 h-12 text-burnt-coral mb-4" />
                  <h3 className="text-xl font-bold text-rabbit mb-2">Instant Decision</h3>
                  <p className="text-chocolate-malt">
                    Apply online in 2 minutes and receive an immediate response.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all">
                  <Shield className="w-12 h-12 text-burnt-coral mb-4" />
                  <h3 className="text-xl font-bold text-rabbit mb-2">No Hidden Fees</h3>
                  <p className="text-chocolate-malt">
                    No arrangement fees, no early repayment charges, no late payment fees.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all">
                  <Smile className="w-12 h-12 text-burnt-coral mb-4" />
                  <h3 className="text-xl font-bold text-rabbit mb-2">Flexible Terms</h3>
                  <p className="text-chocolate-malt">
                    Choose from 3 to 60 months to find a payment plan that suits you.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-rabbit text-center mb-12">
                Frequently Asked Questions
              </h2>

              <div className="space-y-6">
                <div className="border-b border-sky-gray/30 pb-6">
                  <h3 className="text-xl font-bold text-rabbit mb-3">Who is eligible for finance?</h3>
                  <p className="text-chocolate-malt">
                    You must be over 18, a UK resident, and earn a regular income. Credit decisions are subject to your individual circumstances and affordability checks.
                  </p>
                </div>

                <div className="border-b border-sky-gray/30 pb-6">
                  <h3 className="text-xl font-bold text-rabbit mb-3">Will applying affect my credit score?</h3>
                  <p className="text-chocolate-malt">
                    The initial application uses a soft credit check, which won't affect your score. Only if you accept the finance offer will a hard credit check be performed.
                  </p>
                </div>

                <div className="border-b border-sky-gray/30 pb-6">
                  <h3 className="text-xl font-bold text-rabbit mb-3">How much can I borrow?</h3>
                  <p className="text-chocolate-malt">
                    Finance is available for treatments costing between £500 and £30,000, subject to approval.
                  </p>
                </div>

                <div className="border-b border-sky-gray/30 pb-6">
                  <h3 className="text-xl font-bold text-rabbit mb-3">Can I pay off my loan early?</h3>
                  <p className="text-chocolate-malt">
                    Yes! There are no early repayment charges. You can clear your balance at any time without penalty.
                  </p>
                </div>

                <div className="pb-6">
                  <h3 className="text-xl font-bold text-rabbit mb-3">What treatments are covered?</h3>
                  <p className="text-chocolate-malt">
                    Finance is available for most dental treatments including Invisalign, dental implants, cosmetic bonding, crowns, and comprehensive smile makeovers.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-burnt-coral via-burnt-coral to-golden-sand text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Get Started?
              </h2>
              <p className="text-xl mb-8 text-white/90">
                Book a consultation or apply for finance directly through Tabeo.
              </p>
              
              <div className="flex flex-wrap gap-4 justify-center">
                <a 
                  href="https://lead.tabeo.co.uk/perfect-smile-acton/finance"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-burnt-coral px-8 py-4 rounded-full font-bold text-lg hover:bg-sky-gray transition-all shadow-lg hover:shadow-xl hover:scale-105"
                >
                  Apply for Finance
                </a>
                
                <MiniConsultButton />
              </div>

              <p className="text-sm text-white/70 mt-8 max-w-2xl mx-auto">
                By clicking "Apply for Finance" you will be redirected to Tabeo, our trusted finance provider. 
                Tabeo is a trading name of Novuna Personal Finance, a brand of Mitsubishi HC Capital UK PLC.
              </p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-rabbit text-white py-8">
          <div className="container mx-auto px-4 text-center">
            <p className="text-white/60 text-sm">
              © {new Date().getFullYear()} Restore.Dental. Dr. Kartik Patel. All rights reserved.
            </p>
            <p className="text-white/60 text-xs mt-2">
              Finance provided by Tabeo, subject to status. Terms and conditions apply.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Finance;
